import { ActionIcon, Badge, Button, Group, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowMerge,
  IconPencil,
  IconPlus,
  IconScale,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InitialBalancesDrawer } from 'src/Components/Admin/InitialBalancesDrawer';
import { MemberRoleBadge } from 'src/Components/Admin/MemberRoleBadge';
import { SharesDistributedBar } from 'src/Components/Admin/SharesDistributedBar';
import { BlockedMemberDeletionModal } from 'src/Components/Modals/BlockedMemberDeletionModal';
import { ConfirmMemberDeletionModal } from 'src/Components/Modals/ConfirmMemberDeletionModal';
import { CreatePlaceholderMemberModal } from 'src/Components/Modals/CreatePlaceholderMemberModal';
import { EditMemberGovernanceModal } from 'src/Components/Modals/EditMemberGovernanceModal';
import { EditPermissionsModal } from 'src/Components/Modals/EditPermissionsModal';
import { InviteByEmailModal } from 'src/Components/Modals/InviteByEmailModal';
import { MergePlaceholderMemberModal } from 'src/Components/Modals/MergePlaceholderMemberModal';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { RowAction, RowActions } from 'src/Components/Shared/RowActions';
import { VideoTutorialButton } from 'src/Components/Shared/VideoTutorialButton';
import { useDeleteInvitation } from 'src/Hooks/Mutations/useDeleteInvitation';
import { useDeleteMember } from 'src/Hooks/Mutations/useDeleteMember';
import { useDeletePlaceholderMember } from 'src/Hooks/Mutations/useDeletePlaceholderMember';
import { useResendInvitation } from 'src/Hooks/Mutations/useResendInvitation';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useFetchMemberDeletionCheck } from 'src/Hooks/Queries/useFetchMemberDeletionCheck';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { useOpenInviteTypeModal } from 'src/Hooks/Shared/useOpenInviteTypeModal';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';
import { AssetDetailsDtoCurrentUserRoleEnum, AssetMemberDto } from 'src/Services/API/generated';
import {
  AssetGovernanceFields,
  OperationalRole,
  OwnershipNature,
} from 'src/types/governance.types';
import { getStructureVocabulary } from 'src/Utils/legalStructureVocabulary';
import { OWNERSHIP_FULL_LABEL, OWNERSHIP_MANTINE_COLOR } from 'src/Utils/ownershipColors';

import styles from './MembersSection.module.scss';

const MembersSection = () => {
  const { t } = useTranslation();
  const { data } = useFetchMembers();
  const assetId = useCurrentAssetId();
  const isMobile = useIsMobile();
  const openInviteTypeModal = useOpenInviteTypeModal();
  const { data: asset } = useFetchAssetDetails();
  const isOwner = asset?.isOwner ?? false;

  const [createPlaceholderModalOpen, createPlaceholderModalActions] = useDisclosure(false);
  const [inviteByEmailModalOpen, inviteByEmailModalActions] = useDisclosure(false);
  const [permissionsModalOpen, permissionsModalActions] = useDisclosure(false);
  const [governanceModalOpen, governanceModalActions] = useDisclosure(false);
  const [initialBalancesDrawerOpen, initialBalancesDrawerActions] = useDisclosure(false);
  const [selectedMember, setSelectedMember] = useState<AssetMemberDto | null>(null);

  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const isGovernanceConfigured = !!governance?.legalStructure;
  const isGestionnaire = governance?.currentUserOperationalRole === OperationalRole.GESTIONNAIRE;
  const vocabulary = getStructureVocabulary(t, governance?.legalStructure);

  const { mutate: deleteInvitation } = useDeleteInvitation();
  const { mutate: resendInvitation } = useResendInvitation();
  const { mutate: deleteMember, isLoading: deleteMemberLoading } = useDeleteMember();
  const { mutate: deletePlaceholder, isLoading: deletePlaceholderLoading } =
    useDeletePlaceholderMember();

  const [memberToDelete, setMemberToDelete] = useState<AssetMemberDto | null>(null);
  const [placeholderToMerge, setPlaceholderToMerge] = useState<AssetMemberDto | null>(null);
  const registeredMembers = useMemo(
    () => (data?.members ?? []).filter(member => !member.isPlaceholder),
    [data]
  );
  const memberToDeleteIsPlaceholder = memberToDelete?.isPlaceholder ?? false;
  // Placeholders never have financial references of their own (see backend), so the
  // deletion-check call — which would otherwise reject placeholder ids — is skipped for them.
  const { data: deletionCheck } = useFetchMemberDeletionCheck(
    memberToDelete && !memberToDeleteIsPlaceholder ? memberToDelete.id : null
  );

  const closeDeletionFlow = () => setMemberToDelete(null);
  const memberToDeleteName = memberToDelete
    ? [memberToDelete.user.firstName, memberToDelete.user.lastName].filter(Boolean).join(' ') ||
      memberToDelete.user.email
    : '';
  const handleConfirmDelete = () => {
    if (!memberToDelete) return;
    if (memberToDeleteIsPlaceholder) {
      deletePlaceholder(memberToDelete.user.id, { onSuccess: () => setMemberToDelete(null) });
    } else {
      deleteMember(memberToDelete.id, { onSuccess: () => setMemberToDelete(null) });
    }
  };

  const assetAboveQuotas = useIsAssetAboveQuotas();

  const memberRecords = useMemo(
    () => [
      ...(data?.members ?? []),
      ...(data?.pendingInvitations.map(invitation => ({
        user: {
          firstName: '',
          lastName: '',
          id: '',
          email: invitation.email,
        },
        role: 'pendingInvitation',
        invitationRole: invitation.role,
        id: invitation.id,
      })) ?? []),
    ],
    [data]
  );

  const {
    sortedRecords: sortedMembers,
    sortStatus,
    setSortStatus,
  } = useSortableRecords(memberRecords);

  const handleEditPermissions = (member: AssetMemberDto) => {
    setSelectedMember(member);
    permissionsModalActions.open();
  };

  const handleClosePermissionsModal = () => {
    permissionsModalActions.close();
    setSelectedMember(null);
  };

  const [isEditingOwnerGovernance, setIsEditingOwnerGovernance] = useState(false);

  const handleEditGovernance = (member: AssetMemberDto, isOwnerEdit = false) => {
    setSelectedMember(member);
    setIsEditingOwnerGovernance(isOwnerEdit);
    governanceModalActions.open();
  };

  const handleCloseGovernanceModal = () => {
    governanceModalActions.close();
    setSelectedMember(null);
    setIsEditingOwnerGovernance(false);
  };

  const isPercentageMode = governance?.shareType === 'PERCENTAGE';
  const shareUnit = isPercentageMode ? '%' : 'parts';

  return (
    <>
      <PageHeader
        label={vocabulary.membersTitle}
        rightElement={
          <Group>
            <VideoTutorialButton videoUrl={'https://youtu.be/ooqsltOszIA'} />
            {isGestionnaire && (
              <Button variant="outline" onClick={initialBalancesDrawerActions.open}>
                {t('admin.initialBalances.action', 'Arrêter les comptes')}
              </Button>
            )}
            {isOwner && (
              <Button
                leftSection={<IconPlus size={20} />}
                onClick={() =>
                  openInviteTypeModal(
                    assetId!,
                    createPlaceholderModalActions.open,
                    inviteByEmailModalActions.open
                  )
                }
                disabled={assetAboveQuotas}
              >
                {vocabulary.addMemberLabel}
              </Button>
            )}
          </Group>
        }
      />
      <SharesDistributedBar />
      <DataTable
        className={styles.membersTable}
        withRowBorders={false}
        style={{ marginTop: 24, width: '100%' }}
        scrollAreaProps={{ type: 'never' }}
        records={sortedMembers}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        noRecordsText={t('members.empty', 'Aucun membre')}
        fetching={data === undefined}
        rowStyle={() => ({ height: 54 })}
        idAccessor={'id'}
        columns={[
          {
            accessor: 'user.firstName',
            title: t('admin.members.name', 'Nom'),
            sortable: true,
            render: record => (
              <div
                style={{
                  width: 'fit-content',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-grid',
                }}
              >
                <Group gap={6} wrap="nowrap">
                  <div className={styles.name}>
                    {[record.user.firstName, record.user.lastName].join(' ')}
                  </div>
                  {(record as unknown as { isPlaceholder?: boolean }).isPlaceholder && (
                    <Badge size="xs" variant="outline" color="gray">
                      {t('admin.placeholderMembers.badge', 'Non-inscrit')}
                    </Badge>
                  )}
                </Group>
              </div>
            ),
          },
          {
            accessor: 'user.email',
            title: t('admin.members.email', 'Email'),
            sortable: true,
            render: ({ user }) => <div className={styles.email}>{user.email || '—'}</div>,
            hidden: isMobile,
          },
          {
            accessor: 'role',
            title: t('admin.members.permissions', 'Permissions'),
            sortable: true,
            render: record => {
              const { role } = record;
              if ((record as unknown as { isPlaceholder?: boolean }).isPlaceholder) {
                return (
                  <Text size="xs" c="dimmed">
                    {t('admin.placeholderMembers.noPermissions', 'Sans objet')}
                  </Text>
                );
              }
              if (role === 'pendingInvitation') {
                const invitationRole = (record as unknown as { invitationRole?: string })
                  .invitationRole;
                const invitationRoleLabels: Record<string, string> = {
                  viewer: t('permissions.presets.viewer', 'Visiteur'),
                  contributor: t('permissions.presets.contributor', 'Contributeur'),
                  manager: t('permissions.presets.manager', 'Gestionnaire'),
                };
                return (
                  <Group gap="xs">
                    <MemberRoleBadge role={role as AssetDetailsDtoCurrentUserRoleEnum} />
                    {invitationRole && (
                      <Badge size="sm" variant="light">
                        {invitationRoleLabels[invitationRole] ?? invitationRole}
                      </Badge>
                    )}
                  </Group>
                );
              }
              if (role === 'owner' || !isOwner) {
                return <MemberRoleBadge role={role as AssetDetailsDtoCurrentUserRoleEnum} />;
              }
              return (
                <Group gap="xs">
                  <MemberRoleBadge role={role as AssetDetailsDtoCurrentUserRoleEnum} />
                  <Tooltip
                    label={t('admin.members.editPermissionsTooltip', 'Modifier les permissions')}
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => handleEditPermissions(record as AssetMemberDto)}
                      aria-label={t(
                        'admin.members.editPermissionsTooltip',
                        'Modifier les permissions'
                      )}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            },
          },
          {
            accessor: 'governance',
            title: t('admin.members.governance', 'Gouvernance'),
            hidden: !isOwner || !isGovernanceConfigured || isMobile,
            render: record => {
              const { role } = record;
              if (role === 'pendingInvitation') return null;

              const memberData = record as unknown as Record<string, unknown>;
              const opRole = memberData.operationalRole as OperationalRole | undefined;
              const shares = memberData.shares as number | undefined;
              const ownershipNature = memberData.ownershipNature as OwnershipNature | undefined;
              const isOwnerRecord = role === 'owner';

              if (!opRole) {
                return (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconScale size={14} />}
                    onClick={() => handleEditGovernance(record as AssetMemberDto, isOwnerRecord)}
                  >
                    {t('admin.members.configureGovernance', 'Configurer')}
                  </Button>
                );
              }

              const roleLabels: Record<OperationalRole, string> = {
                [OperationalRole.GESTIONNAIRE]: 'Gestionnaire',
                [OperationalRole.PROPRIETAIRE]: 'Propriétaire',
                [OperationalRole.VISITEUR]: 'Visiteur',
              };
              const roleColors: Record<OperationalRole, string> = {
                [OperationalRole.GESTIONNAIRE]: 'teal',
                [OperationalRole.PROPRIETAIRE]: 'blue',
                [OperationalRole.VISITEUR]: 'gray',
              };

              const showOwnershipBadge =
                governance?.hasDismemberment &&
                ownershipNature != null &&
                ownershipNature !== OwnershipNature.PLEINE_PROPRIETE;

              return (
                <Tooltip
                  label={t('admin.members.editGovernanceTooltip', 'Modifier la gouvernance')}
                  withArrow
                >
                  <Group
                    gap="xs"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEditGovernance(record as AssetMemberDto, isOwnerRecord)}
                  >
                    <Badge size="sm" color={roleColors[opRole]} variant="light">
                      {roleLabels[opRole]}
                    </Badge>
                    {shares != null && opRole !== OperationalRole.VISITEUR && (
                      <Text size="xs" c="dimmed">
                        {shares}
                        {shareUnit === '%' ? '%' : ` ${shareUnit}`}
                      </Text>
                    )}
                    {showOwnershipBadge && ownershipNature && (
                      <Badge
                        size="xs"
                        variant="outline"
                        color={OWNERSHIP_MANTINE_COLOR[ownershipNature]}
                      >
                        {OWNERSHIP_FULL_LABEL[ownershipNature]}
                      </Badge>
                    )}
                    <IconPencil size={14} color="var(--mantine-color-gray-5)" />
                  </Group>
                </Tooltip>
              );
            },
          },
          {
            accessor: 'actions',
            title: '',
            width: 90,
            render: record => {
              const { role, id, user } = record;
              const isMember = role !== 'pendingInvitation' && role !== 'owner';
              const showDeleteMemberAction = isOwner && isMember;

              if (role === 'pendingInvitation') {
                // MR 428 laid these out by hand; RowActions gives the same two icons plus the
                // deletion confirmation that was missing (the whole point of MR 433).
                const invitationActions: RowAction[] = [
                  {
                    key: 'resend',
                    label: t('admin.invitations.resend', "Renvoyer l'invitation"),
                    icon: <IconSend size={18} />,
                    onClick: () => resendInvitation(id),
                  },
                  {
                    key: 'delete',
                    label: t('common.delete', 'Supprimer'),
                    icon: <IconTrash size={18} />,
                    variant: 'danger',
                    onClick: () => deleteInvitation(id),
                    confirm: {
                      title: t(
                        'admin.invitations.delete.confirmTitle',
                        'Supprimer cette invitation ?'
                      ),
                      message: t(
                        'admin.invitations.delete.confirmMessage',
                        "Voulez-vous vraiment supprimer l'invitation envoyée à {{email}} ? Elle ne pourra plus être utilisée.",
                        { email: user.email }
                      ),
                    },
                  },
                ];

                return <RowActions actions={invitationActions} />;
              }

              if (!showDeleteMemberAction) return null;

              // MR 428 moved governance editing into the dedicated "Gouvernance" column
              // (the whole cell is clickable), so the row keeps deletion only.
              const actions: RowAction[] = [
                {
                  // A co-owner added before they signed up, then invited: one person, two lines.
                  key: 'merge',
                  label: t('admin.placeholderMembers.merge.action', 'Fusionner avec un compte'),
                  icon: <IconArrowMerge size={18} />,
                  hidden: !(record as AssetMemberDto).isPlaceholder,
                  onClick: () => setPlaceholderToMerge(record as AssetMemberDto),
                },
                {
                  key: 'delete',
                  label: t('admin.members.deletion.action', 'Supprimer du bien'),
                  icon: <IconTrash size={18} />,
                  variant: 'danger',
                  // ConfirmMemberDeletionModal already asks for confirmation, so no `confirm` here.
                  onClick: () => setMemberToDelete(record as AssetMemberDto),
                },
              ];

              return <RowActions actions={actions} />;
            },
          },
        ]}
      />
      <EditPermissionsModal
        open={permissionsModalOpen}
        onClose={handleClosePermissionsModal}
        member={selectedMember}
      />
      <EditMemberGovernanceModal
        open={governanceModalOpen}
        onClose={handleCloseGovernanceModal}
        member={selectedMember}
        hasDismemberment={governance?.hasDismemberment ?? false}
        shareType={governance?.shareType ?? null}
        totalShares={governance?.totalShares ?? null}
        isOwner={isEditingOwnerGovernance}
      />
      <CreatePlaceholderMemberModal
        open={createPlaceholderModalOpen}
        onClose={createPlaceholderModalActions.close}
      />
      <InviteByEmailModal open={inviteByEmailModalOpen} onClose={inviteByEmailModalActions.close} />
      <MergePlaceholderMemberModal
        open={!!placeholderToMerge}
        onClose={() => setPlaceholderToMerge(null)}
        placeholder={placeholderToMerge}
        candidates={registeredMembers}
      />
      <InitialBalancesDrawer
        open={initialBalancesDrawerOpen}
        onClose={initialBalancesDrawerActions.close}
      />
      <ConfirmMemberDeletionModal
        open={
          !!memberToDelete && (memberToDeleteIsPlaceholder || deletionCheck?.canDelete === true)
        }
        onClose={closeDeletionFlow}
        onConfirm={handleConfirmDelete}
        memberName={memberToDeleteName}
        isLoading={deleteMemberLoading || deletePlaceholderLoading}
      />
      <BlockedMemberDeletionModal
        open={
          !!memberToDelete && !memberToDeleteIsPlaceholder && deletionCheck?.canDelete === false
        }
        onClose={closeDeletionFlow}
        memberName={memberToDeleteName}
        blockers={deletionCheck?.blockers ?? []}
        assetId={assetId ?? ''}
      />
    </>
  );
};

export default MembersSection;
